import type { CatalogItem, ProductModel } from '@/types/catalog'

/**
 * Returnerar första varianten per färg (unik färgordning first-seen).
 */
export function uniqueColourVariants(variants: CatalogItem[]): CatalogItem[] {
  const seen = new Set<string>()
  const out: CatalogItem[] = []
  for (const v of variants) {
    if (!seen.has(v.Colour)) {
      seen.add(v.Colour)
      out.push(v)
    }
  }
  return out
}

/**
 * Skapar bildens filnamn baserat på SKU, bortser från storlek (sista -XXX).
 * Exempel: SKU "1818-XX-BLK-LG" => "1818-XX-BLK-FRONT.png"
 */
export function imageNameFromSku(sku: string): string {
  // Ta bort sista delen (storlek) och ersätt med "FRONT"
  const parts = sku.split('-')
  if (parts.length <= 1) return `${sku.toLowerCase()}-front.png`
  const core = parts.slice(0, -1).join('-').toLowerCase()
  return `${core}-front.png`
}

/**
 * Skapar BACK-bildens filnamn baserat på SKU, bortser från storlek.
 * Exempel: SKU "1818-XX-BLK-LG" => "1818-XX-BLK-BACK.png"
 */
export function imageNameBackFromSku(sku: string): string {
  const parts = sku.split('-')
  if (parts.length <= 1) return `${sku.toLowerCase()}-back.png`
  const core = parts.slice(0, -1).join('-').toLowerCase()
  return `${core}-back.png`
}

export function imageNameDetailFromSku(sku: string, index: number): string {
  const parts = sku.split('-')
  if (parts.length <= 1) return `${sku.toLowerCase()}-detail${index}.png`
  const core = parts.slice(0, -1).join('-').toLowerCase()
  return `${core}-detail${index}.png`
}

/**
 * Returnerar full bildsökväg för en variant/SKU (FRONT).
 */
export function imagePathForSku(sku: string): string {
  return `/images/products/mockup/tiny/${imageNameFromSku(sku)}`
}

/**
 * Returnerar full bildsökväg för en variant/SKU (BACK).
 */
export function imagePathBackForSku(sku: string): string {
  return `/images/products/mockup/tiny/${imageNameBackFromSku(sku)}`
}

/**
 * För modell och färg: hitta första variant med färg, returnera dess FRONT-bild.
 */
export function imageForColour(model: ProductModel, colour: string): string {
  const variant = model.variants.find(v => v.Colour === colour)
  if (variant?.SKU) {
    return imagePathForSku(variant.SKU)
  }
  // Fallback om inget SKU hittas
  return '/images/placeholder.png'
}

/**
 * För modell och färg: hitta första variant med färg, returnera dess BACK-bild.
 */
export function imageBackForColour(model: ProductModel, colour: string): string {
  const variant = model.variants.find(v => v.Colour === colour)
  if (variant?.SKU) {
    return imagePathBackForSku(variant.SKU)
  }
  // Fallback om inget SKU hittas
  return '/images/placeholder.png'
}

/**
 * Returnerar full bildsökväg för detaljbild baserat på modellens Style och index.
 * T.ex. /images/products/mockup-details/tiny/2622-xx-detail-1.jpg
 */
export function detailImage(model: ProductModel, index: number): string {
  const style = String(model.Style).toLowerCase()
  return `/images/products/mockup-details/tiny/${style}-detail-${index}.jpg`
}

/**
 * Modellens "hero"-bild: först förekommande färg-variant (FRONT).
 */
export function heroImageForModel(model: ProductModel): string {
  const firstVariant = model.variants[0]
  if (firstVariant?.SKU) {
    return imagePathForSku(firstVariant.SKU)
  }
  return '/images/placeholder.png'
}

/**
 * Konverterar en lokal bildadress till en Twicpics-adress för bakgrundsbild i rätt format.
 * DEV: returnerar alltid lokal adress. PROD: twic.pics-format.
 *
 * Exempel: /images/intro/foo.jpg =>
 *   https://kudosales.twic.pics/images/intro/foo.jpg?twic=v1/cover=2880x1728/quality=80
 * Om adressen redan är en Twicpics-URL görs ingen konvertering.
 *
 * NYTT: print-mode väljer dimensioner 1422x2000, annars 1440x832.
 */
export function toTwicpicBgUrl(
  localUrl: string,
  print: boolean = false,
  dimensions?: { width: number; height: number }
): string {
  if (!localUrl || typeof localUrl !== 'string') return ''
  // Om det redan är Twicpics, returnera direkt
  if (localUrl.startsWith('https://kudosales.twic.pics/')) return localUrl
  // DEV: returnera alltid lokal adress
  if (import.meta.dev) {
    //return localUrl.startsWith('/') ? localUrl : '/' + localUrl
  }
  // PROD: Använd twic.pics med rätt dimensioner
  const clean = localUrl.startsWith('/') ? localUrl : '/' + localUrl
  const fallbackDims = print ? '1422x2000' : '2880x1664'
  const dims = dimensions
    ? `${dimensions.width}x${dimensions.height}`
    : fallbackDims

  return `https://kudosales.twic.pics${clean}&toTwicpicBgUrl&twic=v1/cover=${dims}/quality=80`
}

export function toTwicpicBgUrlMobile(
  localUrl: string,
  print: boolean = false,
  dimensions?: { width: number; height: number }
): string {
  if (!localUrl || typeof localUrl !== 'string') return ''
  // Om det redan är Twicpics, returnera direkt
  if (localUrl.startsWith('https://kudosales.twic.pics/')) return localUrl
  // DEV: returnera alltid lokal adress
  if (import.meta.dev) {
    //return localUrl.startsWith('/') ? localUrl : '/' + localUrl
  }
  // PROD: Använd twic.pics med rätt dimensioner
  const clean = localUrl.startsWith('/') ? localUrl : '/' + localUrl
  const fallbackDims = print ? '1422x2000' : '1664x2880(5p,15p)'
  const dims = dimensions
    ? `${dimensions.width}x${dimensions.height}`
    : fallbackDims

  return `https://kudosales.twic.pics${clean}&twic=v1/refit=${dims}`
}

export function toTwicpicUrl(
  localUrl: string,
  print: boolean = false,
  dimensions?: { width: number; height: number }
): string {
  if (!localUrl || typeof localUrl !== 'string') return ''
  // Om det redan är Twicpics, returnera direkt
  if (localUrl.startsWith('https://kudosales.twic.pics/')) return localUrl
  // DEV: returnera alltid lokal adress
  if (import.meta.dev) {
    return localUrl.startsWith('/') ? localUrl : '/' + localUrl
  }
  // PROD: Använd twic.pics med rätt dimensioner
  const clean = localUrl.startsWith('/') ? localUrl : '/' + localUrl
  const fallbackDims = print ? '1422x2000' : '2880x1664'
  const dims = dimensions
    ? `${dimensions.width}x${dimensions.height}`
    : fallbackDims

  return `https://kudosales.twic.pics${clean}&twic=v1/contain=${dims}/quality=80`
}